import { api } from "./api";

// Thin wrappers for COP submodules. Kept flat so each page can import only
// what it needs without pulling a store into the bundle.

export const teamsApi = {
  list: (q) => api.get(`/teams${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => r.data),
  create: (p) => api.post("/teams", p).then((r) => r.data),
  update: (id, p) => api.patch(`/teams/${id}`, p).then((r) => r.data),
  remove: (id) => api.delete(`/teams/${id}`).then((r) => r.data),
  addMember: (id, uid) => api.post(`/teams/${id}/members/${uid}`).then((r) => r.data),
  removeMember: (id, uid) => api.delete(`/teams/${id}/members/${uid}`).then((r) => r.data),
};

export const dealsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
    return api.get(`/deals${q.toString() ? `?${q}` : ""}`).then((r) => r.data);
  },
  stages: () => api.get("/deals/stages").then((r) => r.data.stages),
  create: (p) => api.post("/deals", p).then((r) => r.data),
  update: (id, p) => api.patch(`/deals/${id}`, p).then((r) => r.data),
  remove: (id) => api.delete(`/deals/${id}`).then((r) => r.data),
};

export const invoicesApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
    return api.get(`/invoices${q.toString() ? `?${q}` : ""}`).then((r) => r.data);
  },
  create: (p) => api.post("/invoices", p).then((r) => r.data),
  update: (id, p) => api.patch(`/invoices/${id}`, p).then((r) => r.data),
  remove: (id) => api.delete(`/invoices/${id}`).then((r) => r.data),
};

export const constructionApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || v === false) return;
      q.set(k, v);
    });
    return api.get(`/construction${q.toString() ? `?${q}` : ""}`).then((r) => r.data);
  },
  get: (id) => api.get(`/construction/${id}`).then((r) => r.data),
  create: (p) => api.post("/construction", p).then((r) => r.data),
  update: (id, p) => api.patch(`/construction/${id}`, p).then((r) => r.data),
  remove: (id) => api.delete(`/construction/${id}`).then((r) => r.data),
  summary: () => api.get("/construction-stats/summary").then((r) => r.data),
  exportUrl: () => "/api/construction/export.csv",
  allFiles: () => api.get("/construction-files/all").then((r) => r.data),
  bulkDownloadFiles: (fileIds) =>
    api.post(
      "/construction-files/bulk-download",
      { file_ids: fileIds },
      { responseType: "blob" }
    ),
  listShareLinks: (fileId) =>
    api.get(`/construction-files/${fileId}/share-links`).then((r) => r.data),
  createShareLink: (fileId, opts = {}) =>
    api
      .post(`/construction-files/${fileId}/share-links`, opts)
      .then((r) => r.data),
  revokeShareLink: (linkId) =>
    api.delete(`/share-links/${linkId}`).then((r) => r.data),
};

export const copFilesApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v && q.set(k, v));
    return api.get(`/cop-files${q.toString() ? `?${q}` : ""}`).then((r) => r.data);
  },
  upload: (file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.contactId) fd.append("contact_id", opts.contactId);
    if (opts.displayName) fd.append("display_name", opts.displayName);
    return api
      .post("/cop-files", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  rename: (id, name) => api.patch(`/cop-files/${id}`, { name }).then((r) => r.data),
  remove: (id) => api.delete(`/cop-files/${id}`).then((r) => r.data),
  downloadUrl: (id) => `/api/cop-files/${id}/download`,
};

// ---- Shared Files (tenant-wide "Ortak Dosyalar") ----
// Folders form a nested tree; files carry optional password + recipient list.
// See backend `routes/shared_files.py` for the full contract.
export const sharedFilesApi = {
  listFolders: () => api.get("/shared-files/folders").then((r) => r.data),
  createFolder: (name, parent_id = null) =>
    api
      .post("/shared-files/folders", { name, parent_id })
      .then((r) => r.data),
  renameFolder: (id, name) =>
    api.patch(`/shared-files/folders/${id}`, { name }).then((r) => r.data),
  removeFolder: (id) =>
    api.delete(`/shared-files/folders/${id}`).then((r) => r.data),
  listFiles: (folder_id = null) => {
    const q = new URLSearchParams();
    if (folder_id) q.set("folder_id", folder_id);
    return api
      .get(`/shared-files/files${q.toString() ? `?${q}` : ""}`)
      .then((r) => r.data);
  },
  upload: (file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.folder_id) fd.append("folder_id", opts.folder_id);
    if (opts.display_name) fd.append("display_name", opts.display_name);
    if (opts.password) fd.append("password", opts.password);
    if (opts.recipient_ids && opts.recipient_ids.length)
      fd.append("recipient_ids", opts.recipient_ids.join(","));
    if (opts.recipient_all) fd.append("recipient_all", "true");
    return api
      .post("/shared-files/files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  verifyPassword: (id, password) =>
    api
      .post(`/shared-files/files/${id}/verify`, { password })
      .then((r) => r.data),
  revealPassword: (id) =>
    api.get(`/shared-files/files/${id}/reveal`).then((r) => r.data),
  getMeta: (id) => api.get(`/shared-files/files/${id}`).then((r) => r.data),
  remove: (id) => api.delete(`/shared-files/files/${id}`).then((r) => r.data),
  moveFiles: (file_ids, folder_id) =>
    api
      .post("/shared-files/files/move", { file_ids, folder_id: folder_id || null })
      .then((r) => r.data),
  bulkDownload: (file_ids) =>
    api.post(
      "/shared-files/files/bulk-download",
      { file_ids },
      { responseType: "blob" },
    ),
  bulkDelete: (file_ids) =>
    api
      .post("/shared-files/files/bulk-delete", { file_ids })
      .then((r) => r.data),
  downloadUrl: (id, password) => {
    const q = new URLSearchParams();
    if (password) q.set("password", password);
    // Relative to the axios `api` instance which already prefixes /api.
    return `/shared-files/files/${id}/download${q.toString() ? `?${q}` : ""}`;
  },
  /** Absolute URL — use when a full URL is needed (e.g. `<a href>` for
   *  browser-native download without touching axios). */
  absoluteDownloadUrl: (id, password) => {
    const q = new URLSearchParams();
    if (password) q.set("password", password);
    return `/api/shared-files/files/${id}/download${q.toString() ? `?${q}` : ""}`;
  },

  // ---- Feature flags — used by the shared FilesWorkspace component ----
  supportsPassword: true,
  // Every tenant member can browse; only super_admin can mutate.
  adminOnlyMutations: true,
};

// ---- Personal Files ("Kişisel Dosyalar") ----
// Same shape as sharedFilesApi so the FilesWorkspace UI component can be
// reused verbatim. The backend enforces per-user isolation.
export const personalFilesApi = {
  listFolders: () => api.get("/cop-files/folders").then((r) => r.data),
  createFolder: (name, parent_id = null) =>
    api
      .post("/cop-files/folders", { name, parent_id })
      .then((r) => r.data),
  renameFolder: (id, name) =>
    api.patch(`/cop-files/folders/${id}`, { name }).then((r) => r.data),
  removeFolder: (id) =>
    api.delete(`/cop-files/folders/${id}`).then((r) => r.data),
  listFiles: (folder_id = null) => {
    const q = new URLSearchParams();
    if (folder_id) q.set("folder_id", folder_id);
    return api
      .get(`/cop-files${q.toString() ? `?${q}` : ""}`)
      .then((r) => r.data);
  },
  upload: (file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.folder_id) fd.append("folder_id", opts.folder_id);
    if (opts.display_name) fd.append("display_name", opts.display_name);
    return api
      .post("/cop-files", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  // Personal files are never password-protected — noop stubs so the shared
  // preview modal can call them without special-casing the mode.
  verifyPassword: () => Promise.resolve({ ok: true }),
  revealPassword: () => Promise.resolve({ password: "" }),
  getMeta: () => Promise.reject(new Error("Not supported for personal files")),
  remove: (id) => api.delete(`/cop-files/${id}`).then((r) => r.data),
  moveFiles: (file_ids, folder_id) =>
    api
      .post("/cop-files/files/move", { file_ids, folder_id: folder_id || null })
      .then((r) => r.data),
  bulkDownload: (file_ids) =>
    api.post(
      "/cop-files/files/bulk-download",
      { file_ids },
      { responseType: "blob" },
    ),
  bulkDelete: (file_ids) =>
    api.post("/cop-files/files/bulk-delete", { file_ids }).then((r) => r.data),
  downloadUrl: (id) => `/cop-files/${id}/download`,
  absoluteDownloadUrl: (id) => `/api/cop-files/${id}/download`,
  supportsPassword: false,
  adminOnlyMutations: false,
};
